/**
 * @fileoverview NoCategoriesWarning - Warning banner for budgets without active categories.
 *
 * Displays a warning message when a budget has no active categories available for
 * the current month, prompting users to navigate to the Categories page to add some.
 *
 * @description This component:
 * - Only shows after categories have loaded at least once (prevents flash during load)
 * - Checks for active categories (not ceased or future-dated)
 * - Filters out user-specific categories (only shows for shared budget categories)
 * - Provides navigation button to Categories page
 *
 * @module components/shared/NoCategoriesWarning
 */
import Loading from '../Loading/Loading';
import styles from './NoCategoriesWarning.module.css';

// Helper function to check if a category is currently active
const isActiveCategory = (cat) => {
  if (!cat.endDate) return true;
  const endDateStr = cat.endDate.split('T')[0];
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return endDateStr > todayStr;
};

function NoCategoriesWarning({ categories, isLoading, hasLoadedOnce, onNavigateToCategories }) {
  // Don't show anything until categories have loaded at least once
  // This prevents the warning from flashing during initial load
  if (!hasLoadedOnce || isLoading) {
    return null;
  }

  const hasActiveCategories = categories
    .filter(cat => cat.userKey === null || cat.userKey === undefined)
    .some(isActiveCategory);

  if (hasActiveCategories) {
    return null;
  }

  return (
    <div className={styles.warning}>
      <span className={styles.message}>
        Please go to Categories and add categories
      </span>
      <button
        onClick={onNavigateToCategories}
        className={styles.button}
      >
        Go to Categories
      </button>
    </div>
  );
}

export default NoCategoriesWarning;
